import {
  endOfReviewWindow,
  formatDate,
  spreadApart,
  toLanes,
  yearDomain,
  type ReviewWindow,
} from "../helpers";

// Where a label beside a wheel begins, and the side it runs out to from there
export interface WheelLabelPlace {
  x: number;
  y: number;
  isOnLeft: boolean;
}

// The point at an angle, clockwise from twelve o'clock, and a distance from the centre
export const polarPoint = (angle: number, radius: number): [number, number] => [
  radius * Math.sin(angle),
  -radius * Math.cos(angle),
];

export const toDegrees = (radians: number) => (radians * 180) / Math.PI;

// The centre line of an arc, to be drawn as a thick stroke
export const arcLine = (
  startAngle: number,
  endAngle: number,
  radius: number,
) => {
  const [startX, startY] = polarPoint(startAngle, radius);
  const [endX, endY] = polarPoint(endAngle, radius);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}`;
};

export const monthStarts = (year: number) =>
  Array.from({ length: 12 }, (_, month) => new Date(year, month, 1));

// For where the year is already on screen and space is tight
export const formatReviewPeriodInYear = ({
  reviewFrom,
  reviewTo,
}: Pick<ReviewWindow, "reviewFrom" | "reviewTo">) =>
  `${formatDate(reviewFrom, "dayMonth")} – ${formatDate(reviewTo, "dayMonth")}`;

// Every review window that is open at some point in the year, in lanes of their own
export const reviewWindowsInYear = (
  reviewWindows: ReviewWindow[],
  year: number,
) => {
  const [yearStart, yearEnd] = yearDomain(year);

  return toLanes(
    reviewWindows.filter(
      ({ reviewFrom, reviewTo }) =>
        reviewFrom < yearEnd && yearStart < endOfReviewWindow(reviewTo),
    ),
  );
};

// One label per angle, measured clockwise from twelve o'clock, on a circle
// around the wheel. Labels on the same side that would touch move apart up and
// down, and then out as far as it takes to stay clear of the circle
export const placeWheelLabels = (
  angles: number[],
  radius: number,
  lineHeight: number,
) => {
  const natural = angles.map((angle, index) => {
    const [x, y] = polarPoint(angle, radius);

    return { index, x, y, isOnLeft: x < 0 };
  });
  const places = new Map<number, WheelLabelPlace>();

  for (const isOnLeft of [false, true]) {
    const side = natural
      .filter((label) => label.isOnLeft === isOnLeft)
      .sort((a, b) => a.y - b.y);
    const ys = spreadApart(
      side.map(({ y }) => y),
      lineHeight,
    );

    side.forEach(({ index, x }, order) => {
      const y = ys[order] ?? 0;
      const reach = Math.max(
        Math.abs(x),
        Math.sqrt(Math.max(0, radius ** 2 - y ** 2)),
      );

      places.set(index, { x: isOnLeft ? -reach : reach, y, isOnLeft });
    });
  }

  return natural.flatMap(({ index }) => places.get(index) ?? []);
};
